import { useState } from "react";
import { toast } from "react-toastify";
import * as yup from "yup";
import { Row, Col } from "react-bootstrap";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload } from "@fortawesome/pro-regular-svg-icons";
import { WebLayout } from "../web-layout";
import { API } from "../../../utils/AxiosInstance";
import { SubmitButton } from "../../../components/buttons/submit-button";
import { Spacer } from "../spacer";
import { isHabitnuApp } from "../../../utils/white-label";
import { ErrorText } from "../../../components/error-text";
import "./NewFeedback.scss";

interface State {
    email: string;
    category: string;
    subject: string;
    message: string;
    file?: FileList;
}

export function NewFeedback() {
    const { t } = useTranslation();
    const [fileError, setFileError] = useState<string | null>(null);
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);

    const schema = yup.object().shape({
        email: yup.string().email().required(t("Email is required")),
        subject: yup.string().required(t("Subject is required")),
        message: yup.string().required(t("Message is required")),
    });

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting },
        setError,
    } = useForm<State>({
        resolver: yupResolver(schema),
    });

    const validateFile = (file: File): string | null => {
        const maxSize = 10 * 1024 * 1024; // 10MB
        const allowedExtensions = /\.(jpg|jpeg|png|pdf|txt|doc)$/i;

        if (file.size > maxSize) {
            return t("File size must be less than 10MB");
        }
        if (!allowedExtensions.test(file.name)) {
            return t(
                "Invalid file type. Allowed types: JPG, PNG, PDF, TXT, DOC."
            );
        }
        return null;
    };

    const [uploadedFileName, setUploadedFileName] = useState<string | null>(
        null
    );

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0];
        if (selectedFile) {
            const error = validateFile(selectedFile);
            if (error) {
                setFileError(error);
                setUploadedFile(null);
                setUploadedFileName(null);
            } else {
                setFileError(null);
                setUploadedFile(selectedFile);
                setUploadedFileName(selectedFile.name); // Update file name
            }
        }
    };

    async function onSubmit(data: State) {
        const formData = new FormData();
        formData.append("email", data.email);
        formData.append("category", data.category);
        formData.append("subject", data.subject);
        formData.append("feedback", data.message);

        if (uploadedFile) {
            formData.append("file", uploadedFile);
        }

        const endpoint = data.email ? "/guestfeedbacks" : "/feedbacks";

        try {
            await API.post(endpoint, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            }).then(() => {
                toast.success(t("feedback.saved_feedback"));
                reset();
            });
        } catch {
            setError("email", {
                type: "server",
                message: t("Error submitting feedback. Please try again."),
            });
        }
    }

    return (
        <WebLayout>
            <Row>
                <Col
                    xs={12}
                    md={{ offset: 2, span: 8 }}
                    lg={{ offset: 2, span: 8 }}
                >
                    <div className="contact-us-container">
                        <div className="login-header-text login-header-label">
                            {t("Contact us")}
                        </div>
                        <div className="subheader-slogan">
                            {t("Need help? We're here to assist you")}
                        </div>

                        <form
                            className="contact-us-form"
                            onSubmit={handleSubmit(onSubmit)}
                        >
                            {/* Email Field */}
                            <div className="login-label">{t("email_id")}</div>
                            <input
                                type="text"
                                placeholder={t("feedback_email_placeholder")}
                                className="form-control field-height mt-2"
                                {...register("email")}
                            />
                            {errors.email && (
                                <ErrorText text={errors.email.message} />
                            )}

                            {/* Category Field */}
                            <div className="form-group mt-2">
                                <label htmlFor="category">
                                    {t("Category")}{" "}
                                    <div className="ml-2">
                                        {errors.category && (
                                            <ErrorText
                                                text={errors.category.message}
                                            />
                                        )}
                                    </div>
                                </label>
                                <select id="category" {...register("category")}>
                                    <option value="">
                                        {t("Choose category")}
                                    </option>
                                    <option value="Food">{t("Food")}</option>
                                    <option value="Learn">{t("Learn")}</option>
                                    <option value="Discussions">
                                        {t("Discussions")}
                                    </option>
                                    <option value="Health">
                                        {t("Health")}
                                    </option>
                                    <option value="Videos">
                                        {t("Videos")}
                                    </option>
                                    <option value="Habits">
                                        {t("Habits")}
                                    </option>
                                    <option value="Other">{t("Other")}</option>
                                </select>
                            </div>

                            {/* Subject Field */}
                            <div className="login-label">{t("Subject")}</div>
                            <input
                                type="text"
                                placeholder={t("Enter the subject")}
                                className="form-control field-height mt-2"
                                {...register("subject")}
                            />
                            {errors.subject && (
                                <ErrorText text={errors.subject.message} />
                            )}

                            {/* Message Field */}
                            <div className="login-label">{t("Message")}</div>
                            <input
                                type="text"
                                placeholder={t("Enter the message")}
                                className="form-control field-height mt-2"
                                {...register("message")}
                            />
                            {errors.message && (
                                <ErrorText text={errors.message.message} />
                            )}

                            {/* File Upload */}
                            <div className="file-upload-section">
                                <label
                                    htmlFor="fileUpload"
                                    className="file-upload-link mt-2"
                                    style={{
                                        color: isHabitnuApp()
                                            ? "#00087a"
                                            : "#000000",
                                    }}
                                >
                                    <FontAwesomeIcon
                                        icon={faUpload}
                                        color={
                                            isHabitnuApp()
                                                ? "#00087a"
                                                : "#000000"
                                        }
                                    />
                                    {t("Add Files")}
                                </label>
                                <input
                                    id="fileUpload"
                                    type="file"
                                    className="custom-file-input"
                                    onChange={handleFileChange}
                                />
                                {uploadedFileName && (
                                    <div className="file-name">
                                        {t("Uploaded File")}: {uploadedFileName}
                                    </div>
                                )}
                                {fileError && <ErrorText text={fileError} />}
                                <small className="form-text text-muted">
                                    {t(
                                        "Supported Formats: JPG, PNG, PDF, TXT, DOC"
                                    )}
                                    <br />
                                    {t("Maximum Size: 10 MB")}
                                </small>
                            </div>

                            {/* Submit Button */}
                            <Spacer height={10} />
                            <SubmitButton
                                isSubmitting={isSubmitting}
                                label={t("Submit")}
                                className={`form-control field-height ${
                                    isHabitnuApp() ? "" : "ada-btn-success"
                                }`}
                            />
                        </form>
                    </div>
                </Col>
            </Row>
        </WebLayout>
    );
}
